<script setup lang="ts">
import audioBg from '@/assets/image/feed/bg-audio.png'
const userStore = useUserStore()
const { musicInfo, isLogin } = storeToRefs(userStore)
const { formatTime } = useOSSUploader()

const current = ref(0)
const audioRef = ref()
const playUrl = ref()
const showList = ref(false)
const volume = ref(1) // 音量
const isShowMusicPlayer = ref(Local.get('isShowMusicPlayer') || false)
const { playing, currentTime, duration } = useMediaControls(audioRef, {
  src: musicInfo.value?.musicList?.length ? musicInfo.value?.musicList[current.value].url : null,
})
const setAudioInfo = (info?: any) => {
  useUserStore().SET_MUSIC_INFO({
    isPlaying: playing.value,
    playUrl: playUrl.value,
    current: info || musicInfo.value?.musicList[current.value],
    isShowMusicPlayer: isShowMusicPlayer.value,
  })
  musicInfo.value?.musicList?.length &&
    musicInfo.value?.musicList.forEach((v: { url: string }, index: number) => {
      if (playUrl.value === v.url) {
        current.value = index
      }
    })
}

watch(volume, () => {
  audioRef.value.volume = volume.value
})
// 设置音频音量
const setVolume = (vol: number) => {
  audioRef.value.volume = vol
  volume.value = vol
}
const pause = () => {
  audioRef.value.pause()
  playing.value = false
  setAudioInfo()
}
const play = () => {
  nextTick(() => {
    pause()
    setTimeout(() => {
      audioRef.value.play()
      playing.value = true
      setAudioInfo()
    }, 100)
  })
}
const nextSong = () => {
  if (current.value + 1 === musicInfo.value?.musicList?.length) {
    current.value = 0
  } else {
    current.value += 1
  }
  play()
}
const previousSong = () => {
  if (current.value === 0) {
    current.value = musicInfo.value?.musicList?.length - 1
  } else {
    current.value -= 1
  }
  play()
}
const chooseMusic = (index: number) => {
  current.value = index
  playUrl.value = musicInfo.value?.musicList[current.value].url
  play()
}
const toggleMusicList = () => {
  showList.value = !showList.value
}
const hideMusicPlay = () => {
  isShowMusicPlayer.value = false
  Local.remove('isShowMusicPlayer')
  pause()
}
watch(currentTime, val => {
  userStore.SET_CURRENT_TIME(val)
})
const open = (info: any, flag?: string) => {
  isShowMusicPlayer.value = true
  Local.set('isShowMusicPlayer', true)
  setAudioInfo(info)
  playUrl.value = info?.url
  if (flag) {
    setTimeout(() => {
      playing.value ? pause() : play()
    }, 100)
  } else {
    setAudioInfo(info)
  }
}
onMounted(() => {
  nextTick(() => {
    if (audioRef.value) {
      audioRef.value.addEventListener(
        'ended',
        () => {
          // 循环播放
          nextSong()
        },
        false,
      )
    }
  })
})
defineExpose({ play, pause, open })
</script>

<template>
  <div v-if="isLogin" class="fixed bottom-19.5 left-0 z-99 w-full sm:bottom-0 sm:df-b sm:b-t">
    <div
      v-if="isShowMusicPlayer || playing"
      class="sm:w-320 sm:h-20 sm:dark:bg-brand sm:bg-white music relative typeface-robotoRegular mx-auto"
    >
      <!-- 播放列表 -->
      <MusicList :show-list="showList" @chooseMusic="chooseMusic" />
      <!-- 播放器 -->
      <div
        class="sm:h-20 sm:py-3 sm:px-0 !sm:bg-transparent sm:backdrop-blur-none sm:b-none flex df-b b w-full h-15 rounded-2 p-1.5 bg-[#303335] bg-op-60 backdrop-blur-[50px] items-center"
      >
        <img
          :src="musicInfo.musicList[current]?.cover || audioBg"
          class="sm:w-14 sm:h-14 w-12 h-12 rounded-1"
          alt="dfans"
        />
        <div class="flex-1 h-full ml-3.5">
          <div class="flex items-center justify-between">
            <div class="sm:w-60 w-20">
              <div class="text-base truncate typeface-robotoMedium">
                {{ musicInfo.musicList[current]?.name }}
              </div>
              <div class="text-sm truncate text-op-40 typeface-robotoRegular">
                <span class="hidden sm:block">{{ musicInfo.musicList[current]?.userName }}</span>
                <span class="block sm:hidden">
                  {{ formatTime(currentTime) }}/{{ formatTime(duration) }}
                </span>
              </div>
            </div>
            <!-- pc -->
            <div class="hidden w-full sm:flex items-center">
              <div class="flex-1 mr-8">
                <div class="flex items-center justify-center">
                  <IconBtn @click="previousSong">
                    <div class="w-6 h-6 icon" i-carbon:skip-back-filled />
                  </IconBtn>
                  <IconBtn v-if="musicInfo?.isPlaying" class="mx-3" @click="pause">
                    <div class="w-6 h-6 icon" i-ph:pause-fill />
                  </IconBtn>
                  <IconBtn v-else class="mx-3" @click="play">
                    <div class="w-6 h-6 icon" i-ph:play-fill />
                  </IconBtn>
                  <IconBtn @click="nextSong">
                    <div class="w-6 h-6 icon" i-carbon:skip-forward-filled />
                  </IconBtn>
                </div>
                <div
                  class="mt-1 text-sm text-brandB typeface-robotoRegular ml-3.25 mr-6 flex items-center"
                >
                  <div>{{ formatTime(currentTime) }}</div>
                  <n-slider
                    v-model:value="currentTime"
                    class="flex-1 mx-2"
                    :step="1"
                    :max="duration"
                    :disabled="duration === 0"
                    :tooltip="false"
                  />
                  <div>
                    {{ formatTime(duration) }}
                  </div>
                </div>
              </div>
              <div
                v-if="volume"
                class="w-6 h-6 cursor-pointer"
                i-carbon:volume-up-filled
                @click="setVolume(0)"
              />
              <div
                v-else
                class="w-6 h-6 cursor-pointer"
                i-carbon:volume-mute-filled
                @click="setVolume(1)"
              />
              <div class="w-36 ml-4 mr-6 volume">
                <n-slider
                  v-model:value="volume"
                  class=""
                  :step="0.1"
                  :min="0"
                  :max="1"
                  :tooltip="false"
                />
              </div>
              <IconBtn @click="toggleMusicList">
                <div
                  class="w-6 h-6 icon"
                  :class="showList ? 'icon-bg-right' : ''"
                  i-ph:playlist-duotone
                />
              </IconBtn>
              <IconBtn class="ml-3" @click="hideMusicPlay">
                <div class="w-8 h-8 icon" i-carbon:close />
              </IconBtn>
            </div>
            <!-- phone -->
            <div class="sm:hidden flex items-center justify-around flex-1">
              <IconBtn @click="previousSong">
                <div class="w-4.5 h-4.5 icon" i-carbon:skip-back-filled />
              </IconBtn>
              <div v-if="musicInfo?.isPlaying" class="icon-box" @click="pause">
                <div class="w-10 h-10 icon" i-ph:pause-circle />
              </div>
              <div v-else class="icon-box" @click="play">
                <div class="w-10 h-10 icon" i-ph:play-circle />
              </div>
              <IconBtn @click="nextSong">
                <div class="w-4.5 h-4.5 icon" i-carbon:skip-forward-filled />
              </IconBtn>
              <IconBtn @click="toggleMusicList">
                <div
                  class="w-4.5 h-4.5 icon"
                  :class="showList ? 'icon-bg-right' : ''"
                  i-ph:playlist-duotone
                />
              </IconBtn>
              <IconBtn @click="hideMusicPlay">
                <div class="text-lg icon" :class="showList ? 'icon-bg-right' : ''" i-carbon:close />
              </IconBtn>
            </div>
          </div>
        </div>
      </div>
      <audio ref="audioRef" :src="musicInfo.musicList[current]?.url" />
    </div>
  </div>
</template>

<style lang="less" scoped>
.music {
  .volume {
    :deep(.n-slider .n-slider-handles .n-slider-handle-wrapper .n-slider-handle) {
      background: #fff !important;
    }
  }

  .transition {
    transition: max-height 0.4s;
  }

  .backdrop-filter-blur {
    backdrop-filter: blur(35px);
  }

  .backdrop-filter-blur1 {
    backdrop-filter: blur(80px);
  }

  &-controls {
    display: none;
  }

  .text-duration {
    transform: scale(0.5);
    margin-left: -52px;
    margin-bottom: -5px;
    margin-top: -5px;
  }

  .el-slider {
    height: fit-content;
  }

  .el-slider__button-wrapper {
    width: 5px;
    height: 5px;
    top: -10px;
  }

  .el-slider__button {
    width: 5px;
    height: 5px;
    background: #667085;
    border-color: #667085;
  }

  .el-slider__runway {
    height: 3px;
    background: #1d2432;
  }

  .el-slider__bar {
    height: 3px;
    background: #667085;
  }
}
</style>
